import { Injectable } from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {CountriesSearch} from '../models/countries-search.interface';
import {PageRequest} from '../../shared/models/page-request.interface';
import {Observable, throwError} from 'rxjs';
import {Page} from '../../shared/models/page.interface';
import {Countries} from '../models/countries.interface';
import {environment} from '../../../environments/environment';
import {ApiHttpResponse} from '../../shared/models/api-http-response.interface';
import {catchError, map} from 'rxjs/operators';
import {User} from '../../kf-data-entry/models/user.interface';
import {MessageService} from '../../shared/services/message.service';

const COUNTRIES_API = environment.apiUrl + 'countries';

@Injectable({
  providedIn: 'root'
})
export class CountriesService {

  constructor(private http: HttpClient, private messageService: MessageService) { }

  static buildSearchParams(search: CountriesSearch): string {
    let searchParams = '';
    if (search.code && search.code !== '') {
      searchParams += `&code=${search.code}`;
    }
    if (search.name && search.name !== '') {
      searchParams += `&name=${search.name}`;
    }
    if (search.region && search.region !== '') {
      searchParams += `&region=${search.region}`;
    }
    if (search.status) {
      searchParams += `&status=${search.status}`;
    }
    return searchParams;
  }

  static getSortCode(sort: string): string {
    switch (sort) {
      case 'open':
        return 'isReportingAvailable';
      default:
        return sort;
    }
  }

  getAll(search: CountriesSearch, pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<Countries>> {
    let url = `${COUNTRIES_API}?size=${pageRequest.size}&page=${pageRequest.page}`;
    const searchParams = CountriesService.buildSearchParams(search);
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${CountriesService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Countries>>(url + searchParams);
  }

  getCountryCoordinators(id: number|undefined, pageRequest: PageRequest = {page: 0, size: 5}): Observable<Page<User>> {
    let url = `${COUNTRIES_API}/coordinators?size=${pageRequest.size}&page=${pageRequest.page}`;
    let params = '';
    if (id) {
      params += '&id=' + id;
    }
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${CountriesService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<User>>(url + params);
  }

  createCountry(dto: Countries): Observable<ApiHttpResponse<Countries>> {
    return this.http.post<ApiHttpResponse<Countries>>(`${COUNTRIES_API}`, dto)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<Countries>) => {
          return res;
        })
      );
  }

  editCountry(dto: Countries): Observable<ApiHttpResponse<Countries>> {
    return this.http.put<ApiHttpResponse<Countries>>(`${COUNTRIES_API}`, dto)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<Countries>) => {
          return res;
        })
      );
  }

  updateCountry(id: number): Observable<ApiHttpResponse<Countries>> {
    return this.http.put<ApiHttpResponse<Countries>>(`${COUNTRIES_API}/update`, id)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<Countries>) => {
          return res;
        })
      );
  }

  deleteCountry(id: number): Observable<HttpResponse<any>> {
    return this.http.delete(`${COUNTRIES_API}/${id}`, {observe: 'response'})
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  uploadPopulation(file: File): Observable<HttpResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<HttpResponse<any>>(`${COUNTRIES_API}/upload`, formData)
      .pipe(
        catchError(err => {
          this.messageService.showMessage(err.error, 'error');
          return throwError(err);
        }),
        map((res: HttpResponse<any>) => {
          return res;
        })
      );
  }
}
